/**
 * SettingsModal keeps content listeners and color controls in its lifetime
 * disposer list even after replacing the HTML. Give each content generation
 * its own lifetime while preserving the modal's persistent handlers.
 */
const configuredModals = new WeakMap();

export function configureSettingsModalCleanup(modal) {
  if (configuredModals.has(modal)) return configuredModals.get(modal);
  const root = modal.root || modal._root;
  const stockChart = modal.get?.('stockChart');
  const originalInit = modal.initContent;
  const originalClear = modal.clear;
  const libraryDisposers = new Set();
  const contentDisposers = new Set();
  let disposed = false;

  const releaseContent = () => {
    const library = [...libraryDisposers];
    const content = [...contentDisposers];
    libraryDisposers.clear();
    contentDisposers.clear();
    // StockControl also registers itself with Root and stockChart.controls.
    // Its dropdown has separate root registrations (including duplicates in
    // DropdownColors) and is not disposed by ColorControl itself.
    const rootOwned = new Set();
    library.forEach(disposer => {
      if (root?._disposers?.includes(disposer)) rootOwned.add(disposer);
      const dropdown = disposer.getPrivate?.('dropdown');
      if (dropdown && dropdown.get?.('control') === disposer) rootOwned.add(dropdown);
    });
    content.forEach(disposer => disposer.dispose());
    library.forEach(disposer => {
      // Entity.dispose iterates this same array. Do not splice it during that
      // iteration; its remaining entries will be disposed normally as well.
      if (modal.isDisposed()) disposer.dispose();
      else modal.removeDispose(disposer);
    });
    rootOwned.forEach(disposer => disposer.dispose());
    const removeOwned = (list, owned) => {
      if (!Array.isArray(list)) return;
      for (let index = list.length - 1; index >= 0; index--) {
        if (owned.has(list[index])) list.splice(index, 1);
      }
    };
    // Root.dispose also iterates its live array; leave it untouched in teardown.
    if (root && !root.isDisposed()) removeOwned(root._disposers, rootOwned);
    if (stockChart && !stockChart.isDisposed()) removeOwned(stockChart.controls, new Set(library));
  };
  const clear = function (...args) {
    releaseContent();
    return originalClear.apply(this, args);
  };
  const initContent = function (...args) {
    const previous = new Set(this._disposers);
    let completed = false;
    try {
      const result = originalInit.apply(this, args);
      completed = true;
      return result;
    } finally {
      this._disposers.forEach(disposer => {
        if (!previous.has(disposer)) libraryDisposers.add(disposer);
      });
      // An opening listener may immediately cancel, or initialization may fail.
      if (!completed || this.isDisposed() || !this.isOpen()) releaseContent();
    }
  };
  modal.initContent = initContent;
  modal.clear = clear;

  // SettingsModal emits done for both Save/close and Cancel/Escape.
  const done = modal.events.on('done', () => modal.clear());
  const owner = {
    add(disposer) {
      if (disposed || modal.isDisposed() || !modal.isOpen()) disposer.dispose();
      else contentDisposers.add(disposer);
      return disposer;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      done.dispose();
      releaseContent();
      if (modal.initContent === initContent) modal.initContent = originalInit;
      if (modal.clear === clear) modal.clear = originalClear;
    },
  };
  modal.addDisposer(owner);
  configuredModals.set(modal, owner);
  return owner;
}
